import { useEffect, useState, useCallback } from "react";
import { useSession } from "../lib/auth-client";
import { getMyUnreadCount } from "../lib/supportApi";

const POLL_MS = 30 * 1000;

export default function useSupportUnread() {
  const { data: session, isPending } = useSession();
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);

  // Platform admins are walled off from tenant routes (the support unread
  // count lives on the tenant API), so polling it as an admin just yields a
  // recurring 401. Skip entirely for admins — they use the admin support API.
  const isAdmin = session?.user?.role === "admin";

  // Incomplete Google signups have a session but no companyId yet (they're
  // still on /complete-signup, and the public-page ChatBot is mounted for
  // them). The tenant API rejects those with a recurring 403 no_tenant, so
  // skip polling until they've finished setting up their workspace.
  const noTenant = !session?.user?.companyId;

  const refresh = useCallback(async () => {
    if (isPending || !session?.user || isAdmin || noTenant) return;
    try {
      const r = await getMyUnreadCount();
      setCount(Number(r?.count) || 0);
      setError(null);
    } catch (e) {
      if (e?.status === 401) return; // session expired — recover on next poll
      setError(e);
    }
  }, [isPending, session, isAdmin, noTenant]);

  useEffect(() => {
    if (isPending || !session?.user || isAdmin || noTenant) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh, isPending, session, isAdmin, noTenant]);

  return { count, refresh, error };
}
